import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { MultiLineEllipsisText } from '@bingads-webui-react/multiline-ellipsis';
import { VideoControls } from '@bingads-webui-react/video-controls';
import { BusinessNameWithAds } from '@bingads-webui-campaign-react/ad-placement-preview';
import { ImageExperienceImage } from '@bingads-webui-campaign-react/image-experience';

import { ResizedVideoView } from '../resized-video-view';
import { ImageExperienceVideo } from '../model/image-experience-video';
import C from '../constants';

export class PreviewGeneral extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this.multiLineTextRefs = [
      React.createRef(),
      React.createRef(),
      React.createRef(),
    ];
    this.state = {
      play: false,
      mouseIn: false,
    };
  }

  componentDidUpdate() {
    this.multiLineTextRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.processText();
      }
    });
  }

  render() {
    const containerClass = `new-responsive-ad-preview-container ${this.props.className}`;
    const hideText = this.props.hideTextWhenPlay && this.state.play && !this.state.mouseIn;
    const thumbnailUrl = _.get(this.props.customThumbnail, 'ThumbnailUrl') || this.props.video.thumbnailUrl;
    return (
      <div className={containerClass} >
        <div
          className="responsive-ad-preview-content"
          style={{ height: this.props.previewContainerHeight }}
          onMouseEnter={() => this.setState({ mouseIn: true })}
          onMouseLeave={() => this.setState({ mouseIn: false })}
        >
          <VideoControls
            width={this.props.previewContainerWidth}
            immersive
            muteByDefault
            showProgressText={this.props.showProgressText}
          >
            <ResizedVideoView
              thumbnailUrl={thumbnailUrl}
              src={this.props.video.Src}
              width={this.props.video.Width}
              height={this.props.video.Height}
              maxWidth={this.props.previewContainerWidth}
              ratio={C.videoAspectRatios[this.props.video.Type].ratio}
              isCurrent={this.props.isCurrent}
              activityLogger={this.props.activityLogger}
              onPlayStatusUpdate={(s) => {
                this.props.activityLogger.signal('play video ad preview', 'video ad preview');
                this.setState({ play: s });
              }}
              hideControls
            />
          </VideoControls>
          <div className="text-container video-ad-text">
            <a
              className="responsive-ad-preview-content-link"
              target="_blank"
              rel="noopener noreferrer"
              href={this.props.previewTexts.url}
            >
              {
              !hideText && this.props.showShortHeadline &&
              <MultiLineEllipsisText
                ref={this.multiLineTextRefs[0]}
                className="short-headline"
                text={this.props.previewTexts.shortHeadline}
              />
              }
              {
              !hideText && this.props.showLongHeadline &&
              <MultiLineEllipsisText
                ref={this.multiLineTextRefs[1]}
                className="long-headline"
                text={this.props.previewTexts.longHeadline}
              />
              }
            </a>
            {
              !hideText && this.props.showUrl &&
              <MultiLineEllipsisText
                ref={this.multiLineTextRefs[1]}
                className="final-url"
                text={this.props.previewTexts.url}
              />
            }
            {
              !hideText && this.props.showDescription &&
              <MultiLineEllipsisText
                ref={this.multiLineTextRefs[2]}
                className="description"
                text={this.props.previewTexts.description}
              />
            }
            {
              this.props.showBusinessName &&
              <BusinessNameWithAds
                i18n={this.props.i18n}
                businessName={this.props.previewTexts.businessName}
              />
            }
          </div>
        </div>
        <div className="video-ad-preview-type-name">
          {this.props.i18n.getString(`ImageExperience_Video_Ad_Preview_Type_${this.props.type}`)}
        </div>
        {
          this.props.showAdditionalDisclaimer &&
          <div className="additional-disclaimer">
            {this.props.i18n.getString('ImageExperience_Additional_Disclaimer_Message')}
          </div>
        }
      </div>
    );
  }
}

PreviewGeneral.propTypes = {
  video: PropTypes.instanceOf(ImageExperienceVideo).isRequired,
  customThumbnail: PropTypes.instanceOf(ImageExperienceImage),
  previewTexts: PropTypes.shape({
    shortHeadline: PropTypes.string,
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    businessName: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  hideTextWhenPlay: PropTypes.bool,
  showShortHeadline: PropTypes.bool,
  showLongHeadline: PropTypes.bool,
  showDescription: PropTypes.bool,
  showAdditionalDisclaimer: PropTypes.bool,
  showProgressText: PropTypes.bool,
  previewContainerHeight: PropTypes.number.isRequired,
  previewContainerWidth: PropTypes.number.isRequired,
  additionalSetting: PropTypes.shape({
    previewTypeStringKey: PropTypes.string,
    imagePreviewHeight: PropTypes.number,
  }),
  showBusinessName: PropTypes.bool,
  showUrl: PropTypes.bool,
  isCurrent: PropTypes.bool,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
};

PreviewGeneral.defaultProps = {
  hideTextWhenPlay: false,
  showShortHeadline: false,
  showLongHeadline: false,
  showDescription: false,
  showAdditionalDisclaimer: false,
  showProgressText: true,
  additionalSetting: null,
  showBusinessName: true,
  showUrl: false,
  isCurrent: false,
  activityLogger: {
    signal: _.noop,
    error: _.noop,
  },
  customThumbnail: null,
};
